/**
 * PropertyGSC — Google Search Console lens for one property.
 * Most authoritative source for search performance — Google's own data.
 */
import { useState } from "react";
import { useParams } from "react-router-dom";
import {
  Globe, Tag, Smartphone, FileCheck, MapPin, Layers,
  Calendar, Sparkles, Image as ImageIcon, Video, Newspaper, Compass,
  Loader2, Search,
} from "lucide-react";
import {
  useProperty, useCapabilitySample, useStartGSCPull,
  useGSCBatchInspect,
} from "@/lib/queries";
import { VendorLens, DataTable, Headline, ExtLink, type Column } from "@/components/VendorLens";
import GenerateReportButton from "@/components/GenerateReportButton";
import { Card } from "@tremor/react";
import { cn } from "@/lib/utils";


export default function PropertyGSC() {
  const { propertyId: rawId } = useParams<{ propertyId: string }>();
  const propertyId = Number(rawId);
  const property = useProperty(propertyId);
  const pull = useStartGSCPull(propertyId);

  if (property.isLoading) return <div className="text-sm text-muted-foreground">Loading…</div>;
  if (!property.data) return <div className="text-sm text-red-600">Property not found.</div>;
  const p = property.data;

  return (
    <VendorLens
      vendorIcon={<Globe className="h-5 w-5 text-primary" />}
      vendorTitle="Google Search Console"
      vendorDescription={
        <>
          Google's own view of {p.domain}'s search performance — queries we
          rank for, pages getting clicks, sitemap status, indexability.
          The most authoritative search-side data we have.
        </>
      }
      pullCostNote="Free (GSC API). Takes ~3-5s."
      pullMutation={pull}
      extraActions={
        // Traffic Report kind — GSC + GA4 + ssh_logs share a Traffic
        // posture, so the button lives here on the GSC page (highest-
        // information vendor for traffic analysis).
        <GenerateReportButton propertyId={propertyId} kind="traffic" />
      }
      tabs={[
        { key: "overview", label: "Overview", body: <OverviewTab propertyId={propertyId} /> },
        { key: "queries",  label: "Queries",  body: <QueriesTab  propertyId={propertyId} domain={p.domain} /> },
        { key: "pages",    label: "Pages",    body: <PagesTab    propertyId={propertyId} domain={p.domain} /> },
        { key: "device",   label: "Device",   body: <DeviceTab   propertyId={propertyId} /> },
        { key: "country",  label: "Country",  body: <CountryTab  propertyId={propertyId} /> },
        { key: "date",     label: "Timeline", body: <DateTab     propertyId={propertyId} /> },
        { key: "appearance", label: "Search appearance", body: <SearchAppearanceTab propertyId={propertyId} /> },
        { key: "surfaces", label: "Surfaces", body: <SurfacesTab propertyId={propertyId} /> },
        { key: "qp",       label: "Query × Page", body: <QueryPageTab propertyId={propertyId} domain={p.domain} /> },
        { key: "sitemaps", label: "Sitemaps", body: <SitemapsTab propertyId={propertyId} /> },
        { key: "inspect",  label: "URL inspection", body: <InspectTab propertyId={propertyId} /> },
        { key: "batch",    label: "Batch inspect", body: <BatchInspectTab propertyId={propertyId} /> },
      ]}
    />
  );
}


function OverviewTab({ propertyId }: { propertyId: number }) {
  const queries = useCapabilitySample("gsc", "search_analytics_query", { propertyId });
  const pages   = useCapabilitySample("gsc", "search_analytics_page",  { propertyId });
  const sitemaps = useCapabilitySample("gsc", "sitemaps", { propertyId });

  const qRows = ((queries.data?.response as Record<string, unknown> | undefined)?.rows as Array<Record<string, unknown>> | undefined) ?? [];
  const pRows = ((pages.data?.response as Record<string, unknown> | undefined)?.rows as Array<Record<string, unknown>> | undefined) ?? [];
  const smRows = ((sitemaps.data?.response as Record<string, unknown> | undefined)?.sitemaps as Array<Record<string, unknown>> | undefined) ?? [];

  const totalClicks = qRows.reduce((s, r) => s + Number(r.clicks ?? 0), 0);
  const totalImpressions = qRows.reduce((s, r) => s + Number(r.impressions ?? 0), 0);
  const avgCtr = totalImpressions > 0 ? (totalClicks / totalImpressions) : 0;
  const avgPos = qRows.length > 0
    ? qRows.reduce((s, r) => s + Number(r.position ?? 0), 0) / qRows.length
    : 0;

  return (
    <div className="space-y-3">
      <div className="grid grid-cols-2 gap-3 sm:grid-cols-4">
        <Headline label="Total clicks (28d)"     value={totalClicks} />
        <Headline label="Total impressions"      value={totalImpressions} />
        <Headline label="Avg CTR"                value={`${(avgCtr * 100).toFixed(2)}%`} />
        <Headline label="Avg position"           value={avgPos > 0 ? avgPos.toFixed(1) : undefined} />
      </div>
      <div className="grid grid-cols-2 gap-3 sm:grid-cols-3">
        <Headline label="Distinct queries"       value={qRows.length} />
        <Headline label="Distinct pages"         value={pRows.length} />
        <Headline label="Sitemaps submitted"     value={smRows.length} />
      </div>
    </div>
  );
}


function rowKey(r: Record<string, unknown>): string {
  const keys = r.keys as string[] | undefined;
  return keys?.[0] ?? "—";
}


function QueriesTab({ propertyId, domain: _domain }: { propertyId: number; domain: string }) {
  const sample = useCapabilitySample("gsc", "search_analytics_query", { propertyId });
  const rows = (((sample.data?.response as Record<string, unknown> | undefined)?.rows) as Array<Record<string, unknown>> | undefined) ?? [];
  const sorted = [...rows].sort((a, b) => Number(b.clicks ?? 0) - Number(a.clicks ?? 0));
  return (
    <DataTable
      title="Top search queries (last 28 days)"
      subtitle="Sorted by clicks descending. Top 1000 queries."
      sample={sample.data} rowCount={sorted.length}
      icon={<Tag className="h-5 w-5 text-primary" />}
      columns={queryCols("Query", rowKey)}
      rows={sorted}
    />
  );
}


function PagesTab({ propertyId, domain: _domain }: { propertyId: number; domain: string }) {
  const sample = useCapabilitySample("gsc", "search_analytics_page", { propertyId });
  const rows = (((sample.data?.response as Record<string, unknown> | undefined)?.rows) as Array<Record<string, unknown>> | undefined) ?? [];
  const sorted = [...rows].sort((a, b) => Number(b.clicks ?? 0) - Number(a.clicks ?? 0));
  return (
    <DataTable
      title="Top landing pages (last 28 days)"
      subtitle="Sorted by clicks descending. Top 1000 pages."
      sample={sample.data} rowCount={sorted.length}
      icon={<Globe className="h-5 w-5 text-primary" />}
      columns={[
        { key: "url", label: "URL", className: "font-mono text-[11px] max-w-[40%]",
          render: (r) => <ExtLink href={rowKey(r)} /> },
        ...metricCols(),
      ]}
      rows={sorted}
    />
  );
}


function DeviceTab({ propertyId }: { propertyId: number }) {
  const sample = useCapabilitySample("gsc", "search_analytics_device", { propertyId });
  const rows = (((sample.data?.response as Record<string, unknown> | undefined)?.rows) as Array<Record<string, unknown>> | undefined) ?? [];
  return (
    <DataTable
      title="Performance by device"
      subtitle="Mobile-first ranking — large CTR gap between mobile and desktop signals UX issues."
      sample={sample.data} rowCount={rows.length}
      icon={<Smartphone className="h-5 w-5 text-primary" />}
      columns={[
        { key: "device", label: "Device", render: rowKey },
        ...metricCols(),
      ]}
      rows={rows}
    />
  );
}


function CountryTab({ propertyId }: { propertyId: number }) {
  const sample = useCapabilitySample("gsc", "search_analytics_country", { propertyId });
  const rows = (((sample.data?.response as Record<string, unknown> | undefined)?.rows) as Array<Record<string, unknown>> | undefined) ?? [];
  const sorted = [...rows].sort((a, b) => Number(b.clicks ?? 0) - Number(a.clicks ?? 0));
  return (
    <DataTable
      title="Performance by country"
      subtitle="Geographic distribution of search traffic. Mismatch with target country = SEO opportunity."
      sample={sample.data} rowCount={sorted.length}
      icon={<MapPin className="h-5 w-5 text-primary" />}
      columns={[
        { key: "country", label: "Country", render: rowKey },
        ...metricCols(),
      ]}
      rows={sorted}
    />
  );
}


function QueryPageTab({ propertyId, domain: _domain }: { propertyId: number; domain: string }) {
  const sample = useCapabilitySample("gsc", "search_analytics_query_page", { propertyId });
  const rows = (((sample.data?.response as Record<string, unknown> | undefined)?.rows) as Array<Record<string, unknown>> | undefined) ?? [];
  const sorted = [...rows].sort((a, b) => Number(b.clicks ?? 0) - Number(a.clicks ?? 0));
  return (
    <DataTable
      title="Query × Page"
      subtitle="Per-keyword landing pages. Lets you see exactly which page Google ranks for each query."
      sample={sample.data} rowCount={sorted.length}
      icon={<Layers className="h-5 w-5 text-primary" />}
      columns={[
        { key: "query", label: "Query", className: "max-w-[26%] truncate",
          render: (r) => {
            const ks = r.keys as string[] | undefined;
            return ks?.[0] ?? "—";
          }},
        { key: "page", label: "Landing page", className: "font-mono text-[11px] max-w-[34%]",
          render: (r) => {
            const ks = r.keys as string[] | undefined;
            return <ExtLink href={ks?.[1] ?? ""} />;
          }},
        ...metricCols(),
      ]}
      rows={sorted}
    />
  );
}


function SitemapsTab({ propertyId }: { propertyId: number }) {
  const sample = useCapabilitySample("gsc", "sitemaps", { propertyId });
  const rows = (((sample.data?.response as Record<string, unknown> | undefined)?.sitemaps) as Array<Record<string, unknown>> | undefined) ?? [];
  return (
    <DataTable
      title="Submitted sitemaps"
      subtitle="What Google knows about + when last downloaded + warning/error counts."
      sample={sample.data} rowCount={rows.length}
      icon={<FileCheck className="h-5 w-5 text-primary" />}
      columns={[
        { key: "path", label: "Sitemap URL", className: "font-mono text-[11px]",
          render: (r) => <ExtLink href={String(r.path ?? "")} /> },
        { key: "type", label: "Type" },
        { key: "lastSubmitted", label: "Submitted", align: "right" },
        { key: "lastDownloaded", label: "Downloaded", align: "right" },
        { key: "errors", label: "Errors", align: "right",
          render: (r) => Number(r.errors ?? 0) },
        { key: "warnings", label: "Warnings", align: "right",
          render: (r) => Number(r.warnings ?? 0) },
      ]}
      rows={rows}
    />
  );
}


function InspectTab({ propertyId }: { propertyId: number }) {
  const sample = useCapabilitySample("gsc", "inspect_url", { propertyId });
  const result = (sample.data?.response as Record<string, unknown> | undefined);
  const idx = (result?.indexStatusResult as Record<string, unknown> | undefined) ?? {};
  return (
    <div className="space-y-3">
      <div className="grid grid-cols-2 gap-3 sm:grid-cols-4">
        <Headline label="Verdict"          value={String(idx.verdict ?? "no data")} />
        <Headline label="Coverage state"   value={String(idx.coverageState ?? "no data")} />
        <Headline label="Indexing state"   value={String(idx.indexingState ?? "no data")} />
        <Headline label="Robots.txt state" value={String(idx.robotsTxtState ?? "no data")} />
      </div>
      <div className="grid grid-cols-2 gap-3 sm:grid-cols-2">
        <Headline label="Google canonical" value={String(idx.googleCanonical ?? "—")} />
        <Headline label="User canonical"   value={String(idx.userCanonical ?? "—")} />
      </div>
      <div className="grid grid-cols-2 gap-3 sm:grid-cols-2">
        <Headline label="Last crawled"     value={String(idx.lastCrawlTime ?? "—")} />
        <Headline label="Page fetch"       value={String(idx.pageFetchState ?? "—")} />
      </div>
    </div>
  );
}


// Shared GSC metrics columns (clicks/impressions/ctr/position)
function metricCols(): Column[] {
  return [
    { key: "clicks",      label: "Clicks",      align: "right",
      render: (r) => Number(r.clicks ?? 0).toLocaleString() },
    { key: "impressions", label: "Impressions", align: "right",
      render: (r) => Number(r.impressions ?? 0).toLocaleString() },
    { key: "ctr",         label: "CTR",         align: "right",
      render: (r) => `${(Number(r.ctr ?? 0) * 100).toFixed(1)}%` },
    { key: "position",    label: "Pos",         align: "right",
      render: (r) => Number(r.position ?? 0).toFixed(1) },
  ];
}

function queryCols(label: string, keyExtractor: (r: Record<string, unknown>) => string): Column[] {
  return [
    { key: "query", label, className: "max-w-[34%] truncate",
      render: keyExtractor },
    ...metricCols(),
  ];
}


// ─── Sprint D.11a — new tab components ─────────────────────────────


function DateTab({ propertyId }: { propertyId: number }) {
  const sample = useCapabilitySample("gsc", "search_analytics_date", { propertyId });
  const rows = (((sample.data?.response as Record<string, unknown> | undefined)?.rows) as Array<Record<string, unknown>> | undefined) ?? [];
  const sorted = [...rows].sort((a, b) => {
    const ka = rowKey(a); const kb = rowKey(b);
    return ka < kb ? 1 : -1;  // most recent first
  });
  return (
    <DataTable
      title="Search performance — 90-day timeline"
      subtitle="Daily clicks/impressions/CTR/position. Look for cliffs (algo updates) or gradual decline."
      sample={sample.data} rowCount={sorted.length}
      icon={<Calendar className="h-5 w-5 text-primary" />}
      columns={[
        { key: "date", label: "Date", render: rowKey },
        ...metricCols(),
      ]}
      rows={sorted}
    />
  );
}


function SearchAppearanceTab({ propertyId }: { propertyId: number }) {
  const sample = useCapabilitySample("gsc", "search_analytics_search_appearance", { propertyId });
  const rows = (((sample.data?.response as Record<string, unknown> | undefined)?.rows) as Array<Record<string, unknown>> | undefined) ?? [];
  const sorted = [...rows].sort((a, b) => Number(b.clicks ?? 0) - Number(a.clicks ?? 0));
  return (
    <DataTable
      title="Search appearance (rich results, Discover, AMP, etc)"
      subtitle="Which SERP feature surfaces are driving impressions. Lost rich-result eligibility shows up here as a row going to 0."
      sample={sample.data} rowCount={sorted.length}
      icon={<Sparkles className="h-5 w-5 text-primary" />}
      columns={[
        { key: "searchAppearance", label: "Search appearance", render: rowKey },
        ...metricCols(),
      ]}
      rows={sorted}
    />
  );
}


type SurfaceKind = "image" | "video" | "news" | "discover";

function SurfacesTab({ propertyId }: { propertyId: number }) {
  const [kind, setKind] = useState<SurfaceKind>("image");
  const image    = useCapabilitySample("gsc", "search_analytics_query_image", { propertyId });
  const video    = useCapabilitySample("gsc", "search_analytics_query_video", { propertyId });
  const news     = useCapabilitySample("gsc", "search_analytics_query_news", { propertyId });
  const discover = useCapabilitySample("gsc", "search_analytics_query_discover", { propertyId });

  const samples: Record<SurfaceKind, typeof image> = { image, video, news, discover };
  const icons: Record<SurfaceKind, React.ReactNode> = {
    image: <ImageIcon className="h-5 w-5 text-primary" />,
    video: <Video className="h-5 w-5 text-primary" />,
    news: <Newspaper className="h-5 w-5 text-primary" />,
    discover: <Compass className="h-5 w-5 text-primary" />,
  };
  const sample = samples[kind];
  const rows = (((sample.data?.response as Record<string, unknown> | undefined)?.rows) as Array<Record<string, unknown>> | undefined) ?? [];
  const sorted = [...rows].sort((a, b) => Number(b.clicks ?? 0) - Number(a.clicks ?? 0));

  const counts: Record<SurfaceKind, number> = {
    image: (((image.data?.response as Record<string, unknown> | undefined)?.rows) as unknown[] | undefined)?.length ?? 0,
    video: (((video.data?.response as Record<string, unknown> | undefined)?.rows) as unknown[] | undefined)?.length ?? 0,
    news:  (((news.data?.response as Record<string, unknown> | undefined)?.rows) as unknown[] | undefined)?.length ?? 0,
    discover: (((discover.data?.response as Record<string, unknown> | undefined)?.rows) as unknown[] | undefined)?.length ?? 0,
  };

  return (
    <div className="space-y-3">
      <div className="flex flex-wrap items-center gap-1.5">
        {(["image", "video", "news", "discover"] as const).map((k) => (
          <button key={k} type="button" onClick={() => setKind(k)}
            className={cn(
              "inline-flex items-center gap-1 rounded-md px-2.5 py-1 text-xs font-medium capitalize",
              kind === k
                ? "bg-primary text-primary-foreground"
                : "bg-muted text-muted-foreground hover:bg-accent hover:text-foreground",
            )}>
            {k}
            <span className={cn(
              "rounded-sm px-1 text-[10px]",
              kind === k ? "bg-primary-foreground/20" : "bg-background/50",
            )}>{counts[k]}</span>
          </button>
        ))}
      </div>
      <DataTable
        title={`${kind[0].toUpperCase()}${kind.slice(1)} search`}
        subtitle={
          kind === "discover"
            ? "Queries surfacing on Google Discover — image-led mobile feed. Often the biggest hidden traffic source."
            : kind === "image"
            ? "Image-search queries — usually huge volume but ~0 CTR. Still worth ranking for: brand visibility + Knowledge Graph signal."
            : kind === "video"
            ? "Video-search queries — separate from web SERP. Tracks how YouTube/embedded videos surface."
            : "News-search queries — only populated if site is in Google News."
        }
        sample={sample.data} rowCount={sorted.length}
        icon={icons[kind]}
        columns={queryCols("Query", rowKey)}
        rows={sorted}
      />
    </div>
  );
}


function BatchInspectTab({ propertyId }: { propertyId: number }) {
  const [limit, setLimit] = useState(20);
  const inspect = useGSCBatchInspect(propertyId);

  const onRun = async () => {
    if (!confirm(
      `Run GSC URL Inspection on the top ${limit} pages from the latest crawl? ` +
      "This is rate-limited (~0.5s per URL) so it'll take ~10 seconds for 20 URLs. " +
      "Cached results from past 24h are reused for free."
    )) return;
    try { await inspect.mutateAsync({ limit }); } catch { /* surfaces inline */ }
  };

  const r = inspect.data;
  const indexedCount = r?.results.filter((x) => x.ok && x.indexed === true).length ?? 0;
  const notIndexedCount = r?.results.filter((x) => x.ok && x.indexed === false).length ?? 0;

  return (
    <div className="space-y-4">
      <Card>
        <div className="flex items-center gap-2">
          <FileCheck className="h-4 w-4 text-primary" />
          <h3 className="text-sm font-semibold">Batch URL inspection</h3>
        </div>
        <p className="mt-1 text-xs text-muted-foreground">
          Inspect top-N pages (by inbound internal links) from the latest Frog crawl via the GSC URL
          Inspection API. Each result tells you Google's view: indexed?, canonical, mobile-friendly,
          rich-result eligibility. Results fold into the <strong>gsc_pages</strong> substrate.
        </p>
        <div className="mt-3 flex items-end gap-2">
          <div>
            <label className="block text-[11px] font-medium text-muted-foreground">URLs to inspect</label>
            <input type="number" min={1} max={100} value={limit}
              onChange={(e) => setLimit(Math.max(1, Math.min(100, Number(e.target.value) || 20)))}
              className="mt-0.5 w-20 rounded-md border border-input bg-background px-2 py-1.5 text-sm font-mono" />
          </div>
          <button type="button" onClick={onRun} disabled={inspect.isPending}
            className="inline-flex items-center gap-1.5 rounded-md bg-primary px-4 py-1.5 text-sm font-medium text-primary-foreground hover:bg-primary/90 disabled:opacity-50">
            {inspect.isPending ? <Loader2 className="h-4 w-4 animate-spin" /> : <Search className="h-4 w-4" />}
            Run batch inspect
          </button>
        </div>
        {inspect.error && (
          <div className="mt-2 rounded-md border border-red-500/40 bg-red-500/10 px-2 py-1 text-xs text-red-700 dark:text-red-400">
            {inspect.error instanceof Error ? inspect.error.message : "Batch inspect failed"}
          </div>
        )}
      </Card>

      {r && (
        <>
          <div className="grid grid-cols-2 gap-3 sm:grid-cols-4">
            <Headline label="Inspected"     value={r.inspected} />
            <Headline label="Indexed"       value={indexedCount} />
            <Headline label="Not indexed"   value={notIndexedCount} />
            <Headline label="Errors"        value={r.errors} />
          </div>
          <DataTable
            title="Inspection results"
            subtitle={`${r.inspected} URLs inspected in ${r.duration_s}s — full results stored in gsc_pages substrate.`}
            sample={{ available: true, fetched_at: new Date().toISOString() }}
            rowCount={r.results.length}
            columns={[
              { key: "url", label: "URL", className: "font-mono text-[11px] max-w-[50%]",
                render: (row) => <ExtLink href={String(row.url ?? "")} /> },
              { key: "indexed", label: "Indexed?", align: "right",
                render: (row) => {
                  if (!row.ok) return <span className="text-red-700 dark:text-red-400">error</span>;
                  if (row.indexed === true) return <span className="text-emerald-700 dark:text-emerald-400">yes</span>;
                  if (row.indexed === false) return <span className="text-amber-700 dark:text-amber-400">no</span>;
                  return "—";
                }},
              { key: "coverage_state", label: "Coverage state",
                render: (row) => String(row.coverage_state ?? "—") },
              { key: "error", label: "Error", className: "max-w-[30%] truncate",
                render: (row) => row.error ? <span className="text-red-700 dark:text-red-400">{String(row.error)}</span> : "—" },
            ]}
            rows={r.results as unknown as Array<Record<string, unknown>>}
          />
        </>
      )}
    </div>
  );
}
